import { cn } from '../../lib/utils';

interface SkeletonProps {
  className?: string;
}

/**
 * Base skeleton component with shimmer animation
 */
export function Skeleton({ className }: SkeletonProps) {
  return (
    <div
      className={cn(
        'animate-shimmer bg-gradient-to-r from-bg-tertiary via-bg-secondary to-bg-tertiary bg-[length:200%_100%] rounded',
        className
      )}
    />
  );
}

/**
 * Skeleton for text content
 */
export function SkeletonText({
  lines = 1,
  className
}: {
  lines?: number;
  className?: string;
}) {
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn(
            'h-4',
            // Last line shorter for natural look
            i === lines - 1 && lines > 1 ? 'w-3/4' : 'w-full'
          )}
        />
      ))}
    </div>
  );
}

/**
 * Skeleton for a card layout
 */
export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div className={cn(
      'bg-bg-secondary rounded-lg border border-border-default p-4 space-y-3',
      className
    )}>
      <div className="flex items-center justify-between">
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-5 w-16" />
      </div>
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-4/5" />
      <div className="flex gap-2 pt-2">
        <Skeleton className="h-6 w-16 rounded-full" />
        <Skeleton className="h-6 w-20 rounded-full" />
      </div>
    </div>
  );
}

/**
 * Skeleton for a table row
 */
export function SkeletonTableRow({
  columns = 5,
  className
}: {
  columns?: number;
  className?: string;
}) {
  return (
    <tr className={cn('border-b border-border-default', className)}>
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <Skeleton
            className={cn(
              'h-4',
              i === 0 ? 'w-32' : i === columns - 1 ? 'w-16' : 'w-24'
            )}
          />
        </td>
      ))}
    </tr>
  );
}

/**
 * Skeleton for a table
 */
export function SkeletonTable({
  rows = 5,
  columns = 5,
  className
}: {
  rows?: number;
  columns?: number;
  className?: string;
}) {
  return (
    <table className={cn('w-full', className)}>
      <thead>
        <tr className="border-b border-border-default">
          {Array.from({ length: columns }).map((_, i) => (
            <th key={i} className="px-4 py-3 text-left">
              <Skeleton className="h-4 w-20" />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }).map((_, i) => (
          <SkeletonTableRow key={i} columns={columns} />
        ))}
      </tbody>
    </table>
  );
}

/**
 * Skeleton for stat cards on dashboard
 */
export function SkeletonStatCard({ className }: { className?: string }) {
  return (
    <div className={cn(
      'bg-bg-secondary rounded-lg border border-border-default p-4',
      className
    )}>
      <div className="flex items-center justify-between mb-2">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-8 w-8 rounded" />
      </div>
      <Skeleton className="h-8 w-16 mb-1" />
      <Skeleton className="h-3 w-24" />
    </div>
  );
}

/**
 * Skeleton for a chart
 */
export function SkeletonChart({
  type = 'bar',
  className
}: {
  type?: 'bar' | 'donut';
  className?: string;
}) {
  if (type === 'donut') {
    return (
      <div className={cn('flex items-center justify-center p-8', className)}>
        <Skeleton className="h-40 w-40 rounded-full" />
      </div>
    );
  }

  // Use fixed widths instead of random for deterministic rendering
  const widths = ['w-3/4', 'w-1/2', 'w-2/3', 'w-5/6', 'w-1/3'];

  return (
    <div className={cn('space-y-2 p-4', className)}>
      {widths.map((width, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-4 w-16" />
          <Skeleton className={cn('h-6 rounded', width)} />
        </div>
      ))}
    </div>
  );
}

/**
 * Skeleton for incident detail header
 */
export function SkeletonIncidentHeader({ className }: { className?: string }) {
  return (
    <div className={cn('space-y-4', className)}>
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-96" />
          <div className="flex gap-2">
            <Skeleton className="h-6 w-20 rounded-full" />
            <Skeleton className="h-6 w-24 rounded-full" />
            <Skeleton className="h-6 w-28 rounded-full" />
          </div>
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-10 w-32 rounded" />
          <Skeleton className="h-10 w-28 rounded" />
        </div>
      </div>
    </div>
  );
}

/**
 * Skeleton for notes list
 */
export function SkeletonNotes({
  count = 3,
  className
}: {
  count?: number;
  className?: string;
}) {
  return (
    <div className={cn('space-y-4', className)}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="bg-bg-secondary rounded-lg border border-border-default p-4 space-y-3"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div>
                <Skeleton className="h-4 w-24 mb-1" />
                <Skeleton className="h-3 w-32" />
              </div>
            </div>
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
          <SkeletonText lines={2} />
        </div>
      ))}
    </div>
  );
}

/**
 * Skeleton for dashboard full layout
 */
export function SkeletonDashboard() {
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Skeleton className="h-8 w-32 mb-2" />
          <Skeleton className="h-4 w-48" />
        </div>
        <div className="flex items-center gap-3">
          <Skeleton className="h-5 w-5 rounded" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>

      {/* Two column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column (2/3) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Stats row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <SkeletonStatCard key={i} />
            ))}
          </div>

          {/* Incidents table card */}
          <div className="bg-bg-secondary rounded-xl border border-border-default p-4">
            <div className="flex items-center justify-between mb-4">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-4 w-20" />
            </div>
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-3 py-2 border-b border-border-light last:border-0">
                  <Skeleton className="h-5 w-16 rounded-full" />
                  <Skeleton className="h-4 flex-1" />
                  <Skeleton className="h-4 w-24" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right column (1/3) */}
        <div className="space-y-6">
          {/* AI Assistant skeleton */}
          <div className="h-[740px] rounded-xl border border-border-default bg-bg-primary overflow-hidden">
            <div className="p-4 border-b border-border-default">
              <div className="flex items-center gap-2">
                <Skeleton className="h-8 w-8 rounded-lg" />
                <div>
                  <Skeleton className="h-4 w-24 mb-1" />
                  <Skeleton className="h-3 w-32" />
                </div>
              </div>
            </div>
            <div className="p-4 flex flex-col items-center justify-center h-[calc(100%-140px)]">
              <Skeleton className="h-14 w-14 rounded-full mb-4" />
              <Skeleton className="h-4 w-40 mb-2" />
              <Skeleton className="h-3 w-56 mb-4" />
              <div className="flex flex-wrap gap-2 justify-center">
                <Skeleton className="h-7 w-28 rounded-full" />
                <Skeleton className="h-7 w-32 rounded-full" />
                <Skeleton className="h-7 w-24 rounded-full" />
              </div>
            </div>
            <div className="p-4 border-t border-border-default">
              <Skeleton className="h-10 w-full rounded-lg" />
            </div>
          </div>

          {/* CVE/News card skeleton */}
          <div className="bg-bg-secondary rounded-xl border border-border-default p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Skeleton className="h-5 w-5 rounded" />
                <Skeleton className="h-5 w-36" />
              </div>
              <Skeleton className="h-2 w-2 rounded-full" />
            </div>
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="p-3 rounded-lg bg-bg-tertiary">
                  <Skeleton className="h-4 w-3/4 mb-2" />
                  <Skeleton className="h-3 w-full mb-1" />
                  <Skeleton className="h-3 w-5/6" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
